import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import classNames from 'classnames';
import { Link } from 'react-router-dom';

import colors from '@packages/core/styles/colors';
import { getLogger } from '@packages/core/logging';
import { AdminHubHeader } from '../components/admin-hub/admin-hub-header';
import { AlertBar, Button, Icon, Typography, LoadingCircle } from '@packages/ui/shared';
import { SelectOption } from '@web/components/select';
import { accountsService, httpClient } from '@web/services/singletons';
import { ReportListItem, ReportType, RsqmDsqmUser, SoaUser } from '@packages/contexts/accounts';
import { InputPickerHelper } from '@web/cvt/components/input-picker-helper';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TablePagination,
    TableRenderer,
    TableRow,
} from '@web/components/table';
import { useAuthState } from '@packages/contexts/auth';

const logger = getLogger('admin');

const DEFAULT_REPORT_DATE_RANGE_LIMIT_MONTHS = 6;
const CVT_STATISTICS_REPORT_TYPE = 'CVT_STATISTICS_REPORT';
const CVT_STATISTICS_REPORT_DATE_RANGE_LIMIT_MONTHS = 12;

function getReportDateRangeLimitMonths(reportType: string): number {
    return reportType === CVT_STATISTICS_REPORT_TYPE
        ? CVT_STATISTICS_REPORT_DATE_RANGE_LIMIT_MONTHS
        : DEFAULT_REPORT_DATE_RANGE_LIMIT_MONTHS;
}

function getDateRangeLimitMessage(limitMonths: number): string {
    return limitMonths === CVT_STATISTICS_REPORT_DATE_RANGE_LIMIT_MONTHS
        ? 'Date range cannot be more than 1 year.'
        : `Date range cannot be more than ${limitMonths} months.`;
}

const useStyles = createUseStyles({
    pageContainer: {
        padding: '32px',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
    inputSection: {
        display: 'flex',
        flexDirection: 'row',
        gap: 24,
        justifyContent: 'flex-start',
        alignItems: 'stretch',
        marginBottom: 24,
    },
    selectGroup: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 24,
    },
    selectWrapper: {
        minWidth: 100,
        maxWidth: 300,
        width: 'clamp(220px, 22vw, 300px)',
        flexGrow: 0,
        flexShrink: 1,
        flexBasis: 'clamp(220px, 22vw, 300px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    filterTypeahead: {
        position: 'relative',
        width: '100%',
        '& .rbt': {
            width: '100%',
        },
        '& .rbt-aux': {
            display: 'none',
        },
        '& .rbt-input-main': {
            height: 41.6,
            paddingLeft: 12,
            paddingRight: 36,
        },
        '& .rbt-input-hint': {
            height: 41.6,
            paddingLeft: 12,
            paddingRight: 36,
        },
        '& .rbt-menu': {
            zIndex: 2000,
            maxHeight: 320,
            overflowY: 'auto',
        },
        '& .rbt-menu > .dropdown-item': {
            paddingLeft: 12,
            paddingRight: 12,
        },
    },
    filterTypeaheadError: {
        position: 'relative',
        width: '100%',
        '& .rbt': {
            width: '100%',
        },
        '& .rbt-aux': {
            display: 'none',
        },
        '& .rbt-input-main': {
            height: 41.6,
            paddingLeft: 12,
            paddingRight: 36,
            borderColor: `${colors.redOne} !important`,
            borderWidth: '2px !important',
            borderStyle: 'solid !important',
            '&:focus': {
                borderColor: `${colors.redOne} !important`,
                boxShadow: `0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important`,
            },
        },
        '& .rbt-input-hint': {
            height: 41.6,
            paddingLeft: 12,
            paddingRight: 36,
            borderColor: `${colors.redOne} !important`,
            borderWidth: '2px !important',
            borderStyle: 'solid !important',
        },
        '& .rbt-menu': {
            zIndex: 2000,
            maxHeight: 320,
            overflowY: 'auto',
        },
        '& .rbt-menu > .dropdown-item': {
            paddingLeft: 12,
            paddingRight: 12,
        },
    },
    filterTypeaheadIcon: {
        position: 'absolute',
        top: 8,
        right: 8,
        width: 24,
        height: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.white,
        borderRadius: 12,
        cursor: 'pointer',
        zIndex: 2,
        color: colors.grayFive,
        '&:hover': {
            backgroundColor: colors.grayOne,
        },
        '&:focus-visible': {
            outline: `2px solid ${colors.blueOne}`,
            outlineOffset: 2,
        },
        '& svg': {
            pointerEvents: 'none',
        },
    },
    filterTypeaheadIconDisabled: {
        cursor: 'default',
        color: colors.grayThree,
        backgroundColor: 'transparent',
        pointerEvents: 'none',
    },
    helperText: {
        marginTop: 16,
    },
    tableSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    statusMessage: {
        color: colors.redOne,
    },
    tableLoader: {
        display: 'flex',
        justifyContent: 'center',
        padding: 24,
    },
    borderedTable: {
        '& table': {
            borderCollapse: 'collapse',
            '& thead tr th:last-child': {
                width: '120px',
            },
            '& thead tr th:first-child': {
                width: '280px',
            },
        },
    },
    tableHeaderGroup: {
        justifyContent: 'center',
        textAlign: 'center',
        backgroundColor: colors.grayOne,
    },
    tableHeaderSub: {
        justifyContent: 'center',
        textAlign: 'center',
        backgroundColor: colors.grayOne,
    },
    relativePosition: {
        position: 'relative',
    },
    tableHeaderSpanTwo: {
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
    },
    tableRow: {
        '& th:last-child': {
            width: '120px',
        },
    },
    tableBodyRow: {
        '& td': {
            borderRight: `1px solid ${colors.grayThree}`,
            borderBottom: `1px solid ${colors.grayThree}`,
        },
        '& td:first-child': {
            borderLeft: `1px solid ${colors.grayThree}`,
        },
    },
    tableCellOuter: {
        textAlign: 'center',
        verticalAlign: 'middle',
        padding: 0,
    },
    tableBodyCell: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        width: '100%',
        height: '100%',
        padding: '8px 12px !important',
        lineHeight: 1.2,
    },
    manageAdminReportsCell: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        alignItems: 'stretch',
        textAlign: 'left',
        width: '100%',
    },
    downloadCell: {
        width: 120,
    },
    editRow: {
        backgroundColor: colors.blueFour,
    },
    usernameSelectWrapper: {
        width: '100%',
        minWidth: 280,
        maxWidth: 500,
        '& .rbt': {
            width: '100%',
        },
        '& .rbt-input-main': {
            height: 40,
            paddingLeft: 12,
            paddingRight: 12,
        },
        '& .rbt-menu': {
            minWidth: 240,
            zIndex: 2000,
        },
        '& .dropdown-menu': {
            minWidth: 280,
            maxHeight: 300,
            padding: '8px 0',
            borderRadius: 3,
            border: `1px solid ${colors.grayThree}`,
            boxShadow: '0px 16px 12px -12px rgba(0, 0, 0, 0.16)',
            overflowY: 'auto !important',
            overflowX: 'hidden !important',
            width: 'var(--dsqm-username-menu-width, auto)',
            maxWidth: 'var(--dsqm-username-menu-width, auto)',
        },
        '& .dropdown-menu::-webkit-scrollbar': {
            width: 10,
        },
        '& .dropdown-menu::-webkit-scrollbar-track': {
            backgroundColor: '#ffffff',
        },
        '& .dropdown-menu::-webkit-scrollbar-thumb': {
            backgroundColor: colors.blueFour,
            borderRadius: 10,
            border: '2px solid #ffffff',
        },
        '& .dropdown-menu > .dropdown-item': {
            padding: '5px 10px',
            height: 40,
            display: 'flex',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            textAlign: 'left',
            '&:hover': {
                backgroundColor: colors.blueFour,
                color: colors.black,
            },
            '& label': {
                display: 'flex',
                alignItems: 'center',
            },
            '&:hover label': {
                color: colors.black,
            },
            '& input[type="checkbox"]': {
                marginRight: 10,
            },
        },
    },
    editActions: {
        display: 'flex',
        justifyContent: 'center',
        gap: 8,
    },
    dateContainer: {
        alignItems: 'flex-start',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        justifyContent: 'flex-start',
        width: '100%',
    },
    dateContainersWrapper: {
        display: 'flex',
        gap: '10px',
    },
    mainContainer: { marginLeft: '-150px', marginTop: '40px' },
    headerItemWrapper: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 8,
        width: '100%',
    },
    headerFilterDropDownWrapper: {
        position: 'absolute',
        width: '100%',
        background: 'rgb(248, 248, 248)',
        zIndex: 5,
        top: '60px',
        left: '0px',
        borderRadius: '5px',
    },
});
type FilterTypeaheadProps = {
    id: string;
    options: SelectOption[];
    value: string;
    placeholder: string;
    disabled?: boolean;
    onValueChange(nextValue: string): void;
    errorMessage?: string;
};

const FilterTypeahead: FC<FilterTypeaheadProps> = ({
    id,
    options,
    value,
    placeholder,
    disabled,
    onValueChange,
    errorMessage,
}) => {
    const classes = useStyles();
    const typeaheadRef = useRef<any>(null);
    const [inputValue, setInputValue] = useState('');
    const previousValueRef = useRef<string>(value);

    const selected = useMemo(() => {
        if (!value) {
            return [];
        }
        const match = options.find((option) => option.value === value);
        return match ? [match] : [];
    }, [options, value]);

    const handleSelectionChange = useCallback(
        (selection: SelectOption[]) => {
            const nextValue = selection[0]?.value ?? '';
            onValueChange(nextValue);
            setInputValue('');
        },
        [onValueChange]
    );

    const handleClear = useCallback(() => {
        if (disabled) return;
        onValueChange('');
        const instance = typeaheadRef.current;
        if (instance) {
            instance.clear?.();
            instance.toggleMenu?.();
            instance.inputNode?.focus?.();
        }
        setInputValue('');
    }, [disabled, onValueChange]);

    const handleToggle = useCallback(() => {
        if (disabled) return;
        const instance = typeaheadRef.current;
        if (instance) {
            instance.toggleMenu?.();
            instance.inputNode?.focus?.();
        }
    }, [disabled]);

    useEffect(() => {
        if (previousValueRef.current && !value) {
            setInputValue('');
            const instance = typeaheadRef.current;
            if (instance) instance.clear?.();
        }
        previousValueRef.current = value;
    }, [value]);

    const hasValue = Boolean(value) || inputValue.length > 0;
    const iconAction = hasValue ? handleClear : handleToggle;
    const iconName = hasValue ? 'x-close' : 'chevron-down';

    const filterOptions = useCallback(
        (option: SelectOption) => {
            const normalized = inputValue.trim().toLowerCase();
            if (!normalized) return true;
            const label = (option.title || option.value || '').toLowerCase();
            return label.includes(normalized);
        },
        [inputValue]
    );

    return (
        <Typeahead<SelectOption>
            id={id}
            ref={typeaheadRef}
            className={
                errorMessage && errorMessage?.length > 0 ? classes.filterTypeaheadError : classes.filterTypeahead
            }
            labelKey="title"
            selected={selected}
            options={options}
            placeholder={placeholder}
            disabled={disabled}
            filterBy={filterOptions}
            onChange={handleSelectionChange}
            onInputChange={setInputValue}
        >
            <div
                className={classNames(classes.filterTypeaheadIcon, {
                    [classes.filterTypeaheadIconDisabled]: disabled,
                })}
            >
                <Icon name={iconName} onPress={disabled ? undefined : iconAction} />
            </div>
        </Typeahead>
    );
};

interface ReportListItemRowModel extends ReportListItem {
    rowId: string;
}

const PAGE_SIZE_OPTIONS = [5, 10, 15, 20] as const;

export const ManageAdminReports: FC = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { token } = useAuthState();

    const [submissionInProgress, setSubmissionInProgress] = useState(false);
    const [loadingReportTypes, setLoadingReportTypes] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [reportTypes, setReportTypes] = useState<ReportType[]>([]);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [selectedReportType, setSelectedReportType] = useState<string>('');
    const [startDate, setStartDate] = useState<string | null>(null);
    const [endDate, setEndDate] = useState<string | null>(null);
    const [reportListLoading, setReportListLoading] = useState(false);
    const [reportListItems, setReportListItems] = useState<ReportListItemRowModel[]>([]);
    const [totalReports, setTotalReports] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [accounts, setAccounts] = useState<SoaUser[]>([]);
    const [selectedUserAccountId, setSelectedUserAccountId] = useState<string>('');
    const [selectedReportTypeFilter, setSelectedReportTypeFilter] = useState<string>('');
    const [filterLoading, setFilterLoading] = useState(false);
    const [filterError, setFilterError] = useState<string | null>(null);

    //validation error
    const [validationError, setValidationError] = useState<Record<string, string>>({});

    const [reportTypeFilterDisplay, setReportTypeFilterDisplay] = useState<boolean>(false);
    const [soaUsernameFilterDisplay, setSoaUsernameFilterDisplay] = useState<boolean>(false);

    const onClickDownloadButton = useCallback(
        (reportId: String) => {
            const options = {
                headers: {
                    'X-Subcomm-Access-Token': token,
                },
            };

            fetch(`${httpClient.baseUrl}/reports/download/${reportId}`, options)
                .then((res) => {
                    if (!res.ok) {
                        throw new Error('Failed to get download information');
                    }
                    return res.json();
                })
                .then((data) => {
                    // data should contain { reportId, downloadUrl }
                    if (data.downloadUrl) {
                        // Extract filename from the downloadUrl
                        let filename = 'report.csv'; // default filename
                        try {
                            const parsedUrl = new URL(data.downloadUrl);
                            const respContentDisp = Array.from(parsedUrl.searchParams).filter(
                                (sp) => sp[0] === 'response-content-disposition'
                            );
                            if (respContentDisp && respContentDisp.length > 0) {
                                const contentDispositionHeader = respContentDisp[0][1];
                                if (contentDispositionHeader) {
                                    let filenameSplited = contentDispositionHeader.split('filename=');
                                    if (filenameSplited && filenameSplited.length > 1) {
                                        filename = filenameSplited[1].split(';')[0].replaceAll('"', '');
                                    }
                                }
                            }
                        } catch (error) {
                            logger.warn('Failed to extract the filename from the report download URL', { error });
                        }

                        // Download the actual file from the downloadUrl
                        return fetch(data.downloadUrl)
                            .then((response) => response.blob())
                            .then((blob) => {
                                const file = window.URL.createObjectURL(blob);
                                let a = document.createElement('a');
                                document.body.appendChild(a);
                                a.setAttribute('style', 'display: none');
                                a.href = file;
                                a.download = filename;
                                a.click();
                                window.URL.revokeObjectURL(file);
                                document.body.removeChild(a);
                            });
                    } else {
                        throw new Error('No download URL provided');
                    }
                })
                .catch((error) => {
                    logger.error(`Failed to download the report [${reportId}]`, { error, reportId });
                    setErrorMessage('Failed to download report. Please try again later.');
                });
        },
        [token]
    );

    const loadReportTypes = useCallback(async () => {
        setLoadingReportTypes(true);
        setLoadError(null);
        try {
            const response = await accountsService.getReportTypes();
            if (!response.success) {
                if (response.aborted) return;
                throw response.data;
            }
            const results = response.data.reportTypes ?? [];
            setReportTypes(results);
        } catch (error: unknown) {
            logger.error('Failed to load report types', { error });
            const apiError = error as { message?: string; globalErrors?: string[] };
            const message =
                (Array.isArray(apiError?.globalErrors) && apiError.globalErrors[0]) ||
                apiError?.message ||
                t(
                    'administration:manageAdminReports.errors.loadFailure',
                    'Unable to load report types. Please try again later.'
                );
            setLoadError(message);
        } finally {
            setLoadingReportTypes(false);
        }
    }, [t]);

    const loadAccounts = useCallback(async () => {
        setFilterLoading(true);
        try {
            const response = await accountsService.getSoaUser();
            if (!response.success) {
                throw response.data;
            }
            const results = response.data.soaUsernames ?? [];
            setAccounts(results);
        } catch (error: unknown) {
            logger.error('Failed to load accounts', { error });
            setAccounts([]);
        } finally {
            setFilterLoading(false);
        }
    }, []);

    const loadReportList = useCallback(async () => {
        setReportListLoading(true);
        setFilterError(null);
        try {
            const selectedAccount = accounts.find((account) => account.accountId === selectedUserAccountId);
            const soaUserName = selectedAccount?.soaUsername || undefined;

            const response = await accountsService.getReportList(
                currentPage + 1,
                pageSize,
                soaUserName,
                selectedReportTypeFilter
            );
            if (response.success) {
                setTotalReports(response.data.total);
                const reportListWithRowIds: ReportListItemRowModel[] = response.data.reports.map((report) => ({
                    ...report,
                    rowId: String(report.reportId),
                }));

                setReportListItems(reportListWithRowIds);
            } else {
                throw response.data;
            }
        } catch (error) {
            logger.error('Failed to load report list', { error });
            const apiError = error as { message?: string; globalErrors?: string[] };
            const message =
                (Array.isArray(apiError?.globalErrors) && apiError.globalErrors[0]) ||
                apiError?.message ||
                'Failed to load reports. Please try again later.';
            setFilterError(message);
            setReportListItems([]);
            setTotalReports(0);
        } finally {
            setReportListLoading(false);
        }
    }, [currentPage, pageSize, selectedUserAccountId, accounts, selectedReportTypeFilter]);

    useEffect(() => {
        void loadReportTypes();
    }, [loadReportTypes]);

    useEffect(() => {
        void loadAccounts();
    }, [loadAccounts]);

    useEffect(() => {
        void loadReportList();
    }, [loadReportList]);

    const reportNameOptions = useMemo<SelectOption[]>(
        () =>
            reportTypes.map((reportType) => ({
                value: reportType.reportType,
                title: reportType.description,
            })),
        [reportTypes]
    );

    const userOptions = useMemo<SelectOption[]>(() => {
        return accounts
            .filter((account) => account.soaUsername) // Only include accounts with SOA usernames
            .map((account) => ({
                value: account.accountId,
                title: `${account.soaUsername} - ${account.firstName} ${account.lastName}`.trim(),
            }))
            .sort((a, b) => a.title.localeCompare(b.title));
    }, [accounts]);

    const onSubmitClick = useCallback(async () => {
        // Clear previous messages
        setSuccessMessage('');
        setErrorMessage('');
        setValidationError({});

        // Validation checks
        if (!selectedReportType) {
            setValidationError({ reportType: 'Please select a report type.' });
            return;
        }

        if (!startDate) {
            setValidationError({ startDate: 'Please select a start date.' });
            return;
        }

        if (!endDate) {
            setValidationError({ endDate: 'Please select an end date.' });
            return;
        }

        const start = new Date(startDate);
        const end = new Date(endDate);

        if (start > end) {
            setValidationError({ startDate: 'Start date cannot be later than end date.' });
            return;
        }

        const dateRangeLimitMonths = getReportDateRangeLimitMonths(selectedReportType);
        const maxEndDate = new Date(start);
        maxEndDate.setMonth(maxEndDate.getMonth() + dateRangeLimitMonths);

        if (end > maxEndDate) {
            setValidationError({ endDate: getDateRangeLimitMessage(dateRangeLimitMonths) });
            return;
        }

        const submitReportTypeBody = {
            startDate: startDate || '',
            endDate: endDate || '',
        };

        setSubmissionInProgress(true);
        try {
            const response = await accountsService.submitReportsData(selectedReportType, submitReportTypeBody);
            if (!response.success) {
                if (response.data && response.data.message) {
                    setErrorMessage(response.data.message);
                    setTimeout(() => {
                        setErrorMessage('');
                    }, 5000);
                    return;
                } else {
                    throw new Error('Submission failed');
                }
            }
            setSuccessMessage(
                'Report request is in progress, you will get notified once completed. Also you can refresh the table to see the status and download.'
            );
            setTimeout(() => {
                setSuccessMessage('');
            }, 5000);
        } catch (error) {
            setErrorMessage('Failed to submit report request. Please try again later.');
        } finally {
            setSubmissionInProgress(false);
        }
    }, [selectedReportType, startDate, endDate]);

    const handlePaginationClick = useCallback((index: number) => {
        setCurrentPage(index);
    }, []);
    const handlePaginationSizeChange = useCallback((size: number) => {
        setPageSize(size);
        setCurrentPage(0); // Reset to first page when page size changes
    }, []);

    const handleRefresh = useCallback(() => {
        setCurrentPage(0);
        setPageSize(10);
        void loadReportList();
    }, [loadReportList]);

    return (
        <>
            {successMessage && (
                <div>
                    <AlertBar
                        success
                        show
                        onClose={() => {
                            setSuccessMessage('');
                        }}
                    >
                        <Typography>{successMessage}</Typography>
                    </AlertBar>
                </div>
            )}
            {errorMessage && (
                <div>
                    <AlertBar
                        error
                        show
                        onClose={() => {
                            setErrorMessage('');
                        }}
                    >
                        <Typography>{errorMessage}</Typography>
                    </AlertBar>
                </div>
            )}
            <AdminHubHeader
                breadcrumbs={[
                    { title: t('user:breadcrumbs.adminHub', 'Administration Hub'), to: '/administration' },
                    { title: t('user:breadcrumbs.manageAdminReports', 'Manage Admin Reports') },
                ]}
            />

            <div className="container-fluid">
                <div className={classes.pageContainer}>
                    <div className={classes.inputSection}>
                        <div className={classes.selectGroup}>
                            <div className={classes.selectWrapper}>
                                <Typography variant="label">
                                    {t('administration:manageAdminReports.labels.reportName', 'Report Name')}
                                </Typography>
                                <FilterTypeahead
                                    id="manage-admin-reports-report-name-filter"
                                    options={reportNameOptions}
                                    value={selectedReportType}
                                    placeholder={t(
                                        'administration:manageAdminReports.placeholders.selectReportName',
                                        'Select a report name'
                                    )}
                                    disabled={loadingReportTypes}
                                    onValueChange={(nextValue) => {
                                        setSelectedReportType(nextValue);
                                        // Clear validation error when user selects a value
                                        if (nextValue && validationError.reportType) {
                                            setValidationError((prev) => ({ ...prev, reportType: '' }));
                                        }
                                    }}
                                    errorMessage={validationError.reportType || ''}
                                />
                                {validationError.reportType && (
                                    <div className={classes.statusMessage}>{validationError.reportType}</div>
                                )}
                            </div>
                        </div>

                        <div className={classes.selectGroup}>
                            <div className={classes.selectWrapper}>
                                <Typography variant="label">
                                    {' '}
                                    {t('administration:manageAdminReports.labels.startDate', 'Start Date')}
                                </Typography>
                                <div className={classes.dateContainersWrapper}>
                                    <div className={classes.dateContainer}>
                                        <InputPickerHelper
                                            type="date"
                                            onChange={(value) => {
                                                setStartDate(value);
                                                // Clear validation error when user selects a value
                                                if (value && validationError.startDate) {
                                                    setValidationError((prev) => ({ ...prev, startDate: '' }));
                                                }
                                            }}
                                            iconName={'calendar'}
                                            data={startDate}
                                            mainContainerClassName={classes.mainContainer}
                                            readonly={false}
                                            datePlaceholder="Select start date"
                                            errorMessage={validationError.startDate || ''}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={classes.selectGroup}>
                            <div className={classes.selectWrapper}>
                                <Typography variant="label">
                                    {' '}
                                    {t('administration:manageAdminReports.labels.endDate', 'End Date')}
                                </Typography>
                                <div className={classes.dateContainersWrapper}>
                                    <div className={classes.dateContainer}>
                                        <InputPickerHelper
                                            type="date"
                                            onChange={(value) => {
                                                setEndDate(value);
                                                // Clear validation error when user selects a value
                                                if (value && validationError.endDate) {
                                                    setValidationError((prev) => ({ ...prev, endDate: '' }));
                                                }
                                            }}
                                            iconName={'calendar'}
                                            data={endDate}
                                            mainContainerClassName={classes.mainContainer}
                                            readonly={false}
                                            datePlaceholder="Select end date"
                                            errorMessage={validationError.endDate || ''}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={classes.selectGroup}>
                            <div
                                className={classes.selectWrapper}
                                style={{ minWidth: 161, width: 'clamp(161px, 15vw, 150px)' }}
                            >
                                <Typography variant="label">
                                    {' '}
                                    {t('administration:manageAdminReports.labels.emptyString', ' ')}
                                </Typography>

                                <Button
                                    title={t('administration:manageAdminReports.buttons.submit', 'Generate Report')}
                                    onPress={onSubmitClick}
                                    disabled={submissionInProgress}
                                />
                            </div>
                        </div>
                    </div>

                    <div className={classes.tableSection}>
                        <div className="d-flex justify-content-start align-items-center">
                            <Typography variant="h4" style={{ marginBottom: 4 }}>
                                {t('administration:manageAdminReports.reportSectionTitle', 'Reports History')}
                            </Typography>
                            <OverlayTrigger
                                placement="bottom"
                                overlay={
                                    <Tooltip id="refresh-tool-tip">
                                        {t('administration:manageAdminReports.buttons.refresh', 'Refresh Reports')}
                                    </Tooltip>
                                }
                            >
                                <Button
                                    data-testid="refresh-button"
                                    variant="ghost-blue"
                                    disabled={reportListLoading}
                                    onPress={handleRefresh}
                                >
                                    <Icon name="refresh" style={{ display: 'flex' }} color="blueOne" />
                                </Button>
                            </OverlayTrigger>
                        </div>
                        {filterError && (
                            <div className={classes.statusMessage}>
                                <Typography variant="default">{filterError}</Typography>
                            </div>
                        )}

                        {filterLoading ? (
                            <div className={classes.tableLoader}>
                                <LoadingCircle size={32} />
                            </div>
                        ) : (
                            <Table useFixedLayout className={classes.borderedTable}>
                                <TableHead>
                                    <TableRow>
                                        <TableHeader className={classes.tableHeaderSub}>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    width: '100%',
                                                }}
                                            >
                                                <div className={classes.headerItemWrapper}>
                                                    <Typography variant="label">Report Name</Typography>

                                                    <OverlayTrigger
                                                        placement="bottom"
                                                        overlay={
                                                            <Tooltip id="refresh-tool-tip">
                                                                {t(
                                                                    'administration:manageAdminReports.buttons.filterReportType',
                                                                    'Filter Report Type'
                                                                )}
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <Button
                                                            data-testid="filter-button-1"
                                                            variant="ghost-blue"
                                                            disabled={reportListLoading}
                                                            onPress={() => {
                                                                setReportTypeFilterDisplay(!reportTypeFilterDisplay);
                                                                setSoaUsernameFilterDisplay(false);
                                                            }}
                                                            style={{ position: 'absolute', right: 0 }}
                                                        >
                                                            <Icon
                                                                name="filter-list"
                                                                style={{ display: 'flex' }}
                                                                color="blueOne"
                                                            />
                                                        </Button>
                                                    </OverlayTrigger>
                                                </div>

                                                {selectedReportTypeFilter && (
                                                    <div className={classes.headerItemWrapper} style={{ gap: 0 }}>
                                                        <Typography variant="caption2">
                                                            {selectedReportTypeFilter}
                                                        </Typography>
                                                    </div>
                                                )}
                                            </div>
                                            {reportTypeFilterDisplay && (
                                                <div className={classes.headerFilterDropDownWrapper}>
                                                    <div>
                                                        <div className={classes.selectGroup}>
                                                            <div className={classes.selectWrapper}>
                                                                <FilterTypeahead
                                                                    id="manage-admin-reports-report-name-filter"
                                                                    options={reportNameOptions}
                                                                    value={selectedReportTypeFilter}
                                                                    placeholder={t(
                                                                        'administration:manageAdminReports.placeholders.selectReportName',
                                                                        'Select a report name'
                                                                    )}
                                                                    disabled={filterLoading}
                                                                    onValueChange={(nextValue) => {
                                                                        setSelectedReportTypeFilter(nextValue);
                                                                        setReportTypeFilterDisplay(false);
                                                                        setSoaUsernameFilterDisplay(false);
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </TableHeader>
                                        <TableHeader className={classes.tableHeaderSub}>
                                            <Typography variant="label">Status</Typography>
                                        </TableHeader>
                                        <TableHeader className={classes.tableHeaderSub}>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    width: '100%',
                                                }}
                                            >
                                                <div className={classes.headerItemWrapper}>
                                                    <Typography variant="label">Requested By</Typography>
                                                    <OverlayTrigger
                                                        placement="bottom"
                                                        overlay={
                                                            <Tooltip id="refresh-tool-tip">
                                                                {t(
                                                                    'administration:manageAdminReports.buttons.filterReportType',
                                                                    'Filter Report Type'
                                                                )}
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <Button
                                                            data-testid="filter-button-1"
                                                            variant="ghost-blue"
                                                            disabled={reportListLoading}
                                                            onPress={() => {
                                                                setSoaUsernameFilterDisplay(!soaUsernameFilterDisplay);
                                                                setReportTypeFilterDisplay(false);
                                                            }}
                                                            style={{ position: 'absolute', right: 0 }}
                                                        >
                                                            <Icon
                                                                name="filter-list"
                                                                style={{ display: 'flex' }}
                                                                color="blueOne"
                                                            />
                                                        </Button>
                                                    </OverlayTrigger>
                                                </div>

                                                {selectedUserAccountId && (
                                                    <div className={classes.headerItemWrapper} style={{ gap: 0 }}>
                                                        <Typography variant="caption2">
                                                            {
                                                                userOptions.find(
                                                                    (option) => option.value === selectedUserAccountId
                                                                )?.title
                                                            }
                                                        </Typography>
                                                    </div>
                                                )}
                                            </div>
                                            {soaUsernameFilterDisplay && (
                                                <div className={classes.headerFilterDropDownWrapper}>
                                                    <div>
                                                        <div className={classes.selectGroup}>
                                                            <div className={classes.selectWrapper}>
                                                                <FilterTypeahead
                                                                    id="manage-dsqms-user-filter"
                                                                    options={userOptions}
                                                                    value={selectedUserAccountId}
                                                                    placeholder={t(
                                                                        'administration:manageAdminReports.placeholders.selectSOAUsername',
                                                                        'Select a SOA Username'
                                                                    )}
                                                                    disabled={filterLoading}
                                                                    onValueChange={(nextValue) => {
                                                                        setSelectedUserAccountId(nextValue);
                                                                        setSoaUsernameFilterDisplay(false);
                                                                        setReportTypeFilterDisplay(false);
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </TableHeader>
                                        <TableHeader className={classes.tableHeaderSub}>
                                            <Typography variant="label">Requested Date</Typography>
                                        </TableHeader>
                                        <TableHeader className={classes.tableHeaderSub}>
                                            <Typography variant="label">Report date range</Typography>
                                        </TableHeader>
                                        <TableHeader
                                            className={classes.tableHeaderSub + ' ' + classes.downloadCell}
                                            fixed={false}
                                        >
                                            <Typography variant="label">Download</Typography>
                                        </TableHeader>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reportListItems.map((rowData) => {
                                        return (
                                            <TableRow key={rowData.rowId} className={classNames(classes.tableBodyRow)}>
                                                <TableCell
                                                    className={classNames(
                                                        classes.tableBodyCell,
                                                        classes.manageAdminReportsCell
                                                    )}
                                                    classNameOuter={classes.tableCellOuter}
                                                >
                                                    <Typography
                                                        variant="labelRegular"
                                                        style={{ textAlign: 'center', width: '100%' }}
                                                    >
                                                        {rowData.reportName}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell
                                                    className={classNames(
                                                        classes.tableBodyCell,
                                                        classes.manageAdminReportsCell
                                                    )}
                                                    classNameOuter={classes.tableCellOuter}
                                                >
                                                    <Typography
                                                        variant="labelRegular"
                                                        style={{ textAlign: 'center', width: '100%' }}
                                                    >
                                                        {rowData.status}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell
                                                    className={classNames(
                                                        classes.tableBodyCell,
                                                        classes.manageAdminReportsCell
                                                    )}
                                                    classNameOuter={classes.tableCellOuter}
                                                >
                                                    <Typography
                                                        variant="labelRegular"
                                                        style={{ textAlign: 'center', width: '100%' }}
                                                    >
                                                        {rowData.firstName}, {rowData.lastName}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell
                                                    className={classNames(
                                                        classes.tableBodyCell,
                                                        classes.manageAdminReportsCell
                                                    )}
                                                    classNameOuter={classes.tableCellOuter}
                                                >
                                                    <Typography
                                                        variant="labelRegular"
                                                        style={{ textAlign: 'center', width: '100%' }}
                                                    >
                                                        {rowData.requestedDate}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell
                                                    className={classNames(
                                                        classes.tableBodyCell,
                                                        classes.manageAdminReportsCell
                                                    )}
                                                    classNameOuter={classes.tableCellOuter}
                                                >
                                                    <Typography
                                                        variant="labelRegular"
                                                        style={{ textAlign: 'center', width: '100%' }}
                                                    >
                                                        {rowData.startDate} - {rowData.endDate}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell
                                                    className={classNames(
                                                        classes.tableBodyCell,
                                                        classes.manageAdminReportsCell,
                                                        classes.downloadCell
                                                    )}
                                                    classNameOuter={classes.tableCellOuter}
                                                >
                                                    {rowData?.status?.toLocaleLowerCase() !== 'completed' ? (
                                                        <Button
                                                            data-testid="refresh-button"
                                                            variant="link"
                                                            disabled={true}
                                                            onPress={() => {}}
                                                        >
                                                            <Icon
                                                                name="download"
                                                                style={{ display: 'flex' }}
                                                                color="blueOne"
                                                            />
                                                        </Button>
                                                    ) : (
                                                        <OverlayTrigger
                                                            placement="bottom"
                                                            overlay={
                                                                <Tooltip id="download-tool-tip">
                                                                    {t(
                                                                        'administration:manageAdminReports.buttons.downloadReport',
                                                                        'Download Report'
                                                                    )}
                                                                </Tooltip>
                                                            }
                                                        >
                                                            <Button
                                                                data-testid="download-button"
                                                                variant="link"
                                                                disabled={false}
                                                                onPress={() => onClickDownloadButton(rowData.reportId)}
                                                            >
                                                                <Icon
                                                                    name="download"
                                                                    style={{ display: 'flex' }}
                                                                    color="blueOne"
                                                                />
                                                            </Button>
                                                        </OverlayTrigger>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        )}
                        <TablePagination
                            page={currentPage}
                            size={pageSize}
                            total={totalReports}
                            onClick={handlePaginationClick}
                            onSizeChange={handlePaginationSizeChange}
                            pageSizeOptions={Array.from(PAGE_SIZE_OPTIONS)}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};
